// RegisterForm.jsx
// import { EuiSelect } from "@elastic/eui";
import React, { useState, useContext } from "react";
import {
  EuiForm,
  EuiFormRow,
  EuiFieldText,
  EuiFieldPassword,
  EuiButton,
  EuiSpacer,
} from "@elastic/eui";
import { UserContext } from "../Context";
import { PostOptions } from "../utils/RequestUtils";
import { useNavigate } from "react-router-dom";
import { routeStudent } from "../utils/studentRouting";
import { getApiUrl } from "../utils/api";

const RegisterForm = () => {
  const [form, setForm] = useState({
    username: "", // Retains 'username' as the key (represents email)
    full_name: "",
    password: "",
    confirmPassword: "",
    // role automatically set to 'student' for all registrations
    role: "student", // Fixed to 'student' for all new registrations
    age: "", // Retained for future use
  });

  const [errors, setErrors] = useState({
    username: null, // Updated to 'username' for email validation
    full_name: null,
    password: null,
    confirmPassword: null,
    general: null,
  });

  const [touched, setTouched] = useState({
    username: false,
    full_name: false,
    password: false,
    confirmPassword: false,
  });

  const navigate = useNavigate();
  const { user, setUser } = useContext(UserContext);

  if (user) {
    navigate("/");
  }

  const validateEmail = (email) => {
    // Simple email regex validation
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const validatePassword = (password) => {
    // Enforce strong password: at least 8 characters, including uppercase, lowercase, number, and special character
    if (password.length < 8) return false;
    
    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[^A-Za-z\d]/.test(password); // Any non-alphanumeric character
    
    return hasLowercase && hasUppercase && hasNumber && hasSpecialChar;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: value,
    });

    // Mark field as touched if it's the first change
    if (!touched[name]) {
      setTouched((prev) => ({ ...prev, [name]: true }));
    }

    // Reset specific field error on change
    setErrors((prev) => ({
      ...prev,
      [name]: null,
      general: null,
    }));

    // Field-specific validations
    switch (name) {
      case "username":
        if (value && !validateEmail(value)) {
          setErrors((prev) => ({
            ...prev,
            username: "Please enter a valid email address.",
          }));
        }
        break;
      case "full_name":
        if (value && value.trim().length < 3) {
          setErrors((prev) => ({
            ...prev,
            full_name: "Full name must be at least 3 characters.",
          }));
        }
        break;
      case "password":
        if (value && !validatePassword(value)) {
          setErrors((prev) => ({
            ...prev,
            password:
              "Password must be at least 8 characters long and include uppercase, lowercase, number, and special character.",
          }));
        }
        // Also check confirmPassword if it has a value
        if (form.confirmPassword && value !== form.confirmPassword) {
          setErrors((prev) => ({
            ...prev,
            confirmPassword: "Passwords do not match.",
          }));
        } else if (form.confirmPassword) {
          setErrors((prev) => ({ ...prev, confirmPassword: null }));
        }
        break;
      case "confirmPassword":
        if (value && value !== form.password) {
          setErrors((prev) => ({
            ...prev,
            confirmPassword: "Passwords do not match.",
          }));
        } else {
          setErrors((prev) => ({ ...prev, confirmPassword: null }));
        }
        break;
      default:
        break;
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    if (!touched[name]) {
      setTouched((prev) => ({ ...prev, [name]: true }));
    }

    // Perform validation on blur
    const value = form[name];
    switch (name) {
      case "username":
        if (value && !validateEmail(value)) {
          setErrors((prev) => ({
            ...prev,
            username: "Please enter a valid email address.",
          }));
        }
        break;
      case "full_name":
        if (value && value.trim().length < 3) {
          setErrors((prev) => ({
            ...prev,
            full_name: "Full name must be at least 3 characters.",
          }));
        }
        break;
      case "password":
        if (value && !validatePassword(value)) {
          setErrors((prev) => ({
            ...prev,
            password:
              "Password must be at least 8 characters long and include uppercase, lowercase, number, and special character.",
          }));
        }
        break;
      case "confirmPassword":
        if (value && value !== form.password) {
          setErrors((prev) => ({
            ...prev,
            confirmPassword: "Passwords do not match.",
          }));
        }
        break;
      default:
        break;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Final validation check before submission
    const currentErrors = {};
    if (!validateEmail(form.username)) {
      currentErrors.username = "Please enter a valid email address.";
    }
    if (form.full_name.trim().length < 3) {
      currentErrors.full_name = "Full name must be at least 3 characters.";
    }
    if (!validatePassword(form.password)) {
      currentErrors.password =
        "Password must be at least 8 characters and include uppercase, lowercase, a number, and a special character.";
    }
    if (form.password !== form.confirmPassword) {
      currentErrors.confirmPassword = "Passwords do not match.";
    }

    if (Object.keys(currentErrors).length > 0) {
      setErrors(currentErrors);
      return;
    }

    try {
      const response = await fetch(getApiUrl("/api/register"), PostOptions(form));
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || "Registration failed");
      }

      setUser(data);
      sessionStorage.setItem("user", JSON.stringify(data));
      if (data.role === "student" || data.role === "" || !data.role) {
        await routeStudent(navigate);
      } else {
        navigate("/courses");
      }
    } catch (error) {
      console.error("Registration error:", error);
      setErrors((prev) => ({ ...prev, general: error.message }));
    }
  };

  return (
    <EuiForm
      component="form"
      onSubmit={handleSubmit}
      isInvalid={!!errors.general}
      error={[errors.general]}
      noValidate
    >
      <EuiSpacer size="m" />
      <EuiFormRow
        label="Email (Preferably NC State Email)"
        isInvalid={errors.username !== null && touched.username}
        error={errors.username}
      >
        <EuiFieldText
          name="username" // Retains 'username' as the name attribute
          value={form.username}
          onChange={handleChange}
          onBlur={handleBlur}
          isInvalid={errors.username !== null && touched.username}
          placeholder="example@ncsu.edu"
          aria-label="Email (Preferably NC State Email)"
          className="modern-input"
        />
      </EuiFormRow>
      <EuiFormRow
        label="Full Name"
        isInvalid={errors.full_name !== null && touched.full_name}
        error={errors.full_name}
      >
        <EuiFieldText
          name="full_name"
          value={form.full_name}
          onChange={handleChange}
          onBlur={handleBlur}
          isInvalid={errors.full_name !== null && touched.full_name}
          aria-label="Full Name"
          className="modern-input"
        />
      </EuiFormRow>
      <EuiFormRow
        label="Password"
        isInvalid={errors.password !== null && touched.password}
        error={errors.password}
      >
        <EuiFieldPassword
          name="password"
          value={form.password}
          onChange={handleChange}
          onBlur={handleBlur}
          isInvalid={errors.password !== null && touched.password}
          aria-label="Password"
          className="modern-input"
        />
      </EuiFormRow>
      <EuiFormRow
        label="Confirm Password"
        isInvalid={errors.confirmPassword !== null && touched.confirmPassword}
        error={errors.confirmPassword}
      >
        <EuiFieldPassword
          name="confirmPassword"
          value={form.confirmPassword}
          onChange={handleChange}
          onBlur={handleBlur}
          isInvalid={errors.confirmPassword !== null && touched.confirmPassword}
          aria-label="Confirm Password"
          className="modern-input"
        />
      </EuiFormRow>
      <EuiSpacer size="m" />
      
      <EuiButton type="submit" fill className="modern-button">
        Register
      </EuiButton>
    </EuiForm>
  );
};

export default RegisterForm;
